import gsap from 'gsap';
import lerp from 'lerp';
import type { RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import useWindowSize from './useWindowSize';
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect';
import useEventListener from './useEventListener';
import type { ScrollProviderContextType } from '../components/unlisted/ScrollProvider';
import { distance } from 'util/math.utils';
import { useScrollProviderContext } from '../components/unlisted/ScrollProvider';

export type MagneticOptions = {
  amt?: {
    x?: number;
    y?: number;
    scale?: number;
  };
  strength?: number;
  distanceMultiplier?: number;
  scale?: number;
};

const defaultMagneticOptions: MagneticOptions = {
  amt: {
    x: 0.1,
    y: 0.1,
    scale: 0.2,
  },
  strength: 0.3,
  distanceMultiplier: 1.5,
  scale: 1.3,
};

export type Interpolation = {
  previous: number;
  current: number;
};

const initialRenderStyles = {
  x: { current: 0, previous: 0 },
  y: { current: 0, previous: 0 },
  scale: { current: 1, previous: 1 },
};

export default function useMagnetic(
  enabled: boolean,
  refs: {
    element: RefObject<HTMLElement | null>;
    scaleElement?: RefObject<HTMLElement | null>;
    innerElement?: RefObject<HTMLElement | null>;
  },
  options?: MagneticOptions,
  isVisible: boolean = true,
): {
  isHover: boolean;
} {
  const { amt, strength, distanceMultiplier, scale } = {
    ...defaultMagneticOptions,
    ...options,
  };
  const renderStyles = useRef<{
    x: Interpolation;
    y: Interpolation;
    scale: Interpolation;
  }>(initialRenderStyles);
  const scrollProviderContext = useScrollProviderContext() as ScrollProviderContextType;
  const elementRect = useRef<{ width: number; height: number; top: number; left: number }>(
    undefined,
  );
  const distanceToTrigger = useRef<number>(0);
  const windowSize = useWindowSize();
  const [isHover, setIsHover] = useState(false);

  const getScroll = useCallback((): { x: number; y: number } => {
    const currentScrollStatus = scrollProviderContext?.scrollStatusRef.current;
    return {
      x: typeof currentScrollStatus?.offset.x !== 'number' ? 0 : currentScrollStatus?.offset.x,
      y: typeof currentScrollStatus?.offset.y !== 'number' ? 0 : currentScrollStatus?.offset.y,
    };
  }, [scrollProviderContext?.scrollStatusRef.current]);

  // Calculate Positions and Size
  const calculateSizeAndPosition = useCallback(() => {
    if (enabled && isVisible) {
      const scroll = getScroll();
      const clientRect = refs.element.current?.getBoundingClientRect();

      elementRect.current = {
        width: clientRect?.width || 0,
        height: clientRect?.height || 0,
        top: (clientRect?.top || 0) + scroll.y,
        left: (clientRect?.left || 0) + scroll.x,
      };

      distanceToTrigger.current =
        (elementRect?.current?.width || 0) * (distanceMultiplier as number);
    }
  }, [
    distanceToTrigger,
    elementRect,
    refs.element.current,
    distanceMultiplier,
    getScroll,
    enabled,
    isVisible,
  ]);

  const resetTween = (
    styles: Array<Interpolation> | Interpolation,
    value: number,
    onUpdate?: () => void,
  ) => {
    gsap.to(styles, {
      previous: value,
      current: value,
      duration: 0.4,
      ease: 'Power2.easeOut',
      onUpdate: () => {
        if (onUpdate) onUpdate();
      },
    });
  };

  const updateElements = useCallback(() => {
    const elementRef = refs.element.current;
    const innerElementRef = refs.innerElement?.current;
    const scaleElementRef = refs.scaleElement?.current;
    const style = renderStyles.current;

    gsap.set(elementRef, {
      x: style.x.previous,
      y: style.y.previous,
    });

    if (innerElementRef) {
      gsap.set(innerElementRef, {
        x: style.x.previous * 0.2,
        y: style.y.previous * 0.2,
      });
    }

    if (scaleElementRef) {
      gsap.set(scaleElementRef, {
        scale: style.scale.previous,
      });
    }
  }, [
    refs.element.current,
    refs.scaleElement?.current,
    refs.innerElement?.current,
    renderStyles.current,
  ]);

  // On MouseMove callback
  const onMouseMove = useCallback(
    (event: MouseEvent) => {
      if (isVisible && enabled) {
        const scroll = getScroll();
        const mousePosition = {
          x: event.clientX,
          y: event.clientY,
        };

        const left = elementRect?.current?.left || 0;
        const width = elementRect?.current?.width || 0;
        const top = elementRect?.current?.top || 0;
        const height = elementRect?.current?.height || 0;

        // Calculate the distance from the mouse to the center of the button
        const distanceMouseButton = distance(
          mousePosition.x + scroll.x,
          mousePosition.y + scroll.y,
          left + width / 2,
          top + height / 2,
        );

        // New values for the translations and scale
        let x: number = 0;
        let y: number = 0;

        if (distanceMouseButton < distanceToTrigger.current) {
          if (!isHover) {
            renderStyles.current.scale.current = scale as number;
            setIsHover(true);
          }

          x = (mousePosition.x + scroll.x - (left + width / 2)) * (strength as number);
          y = (mousePosition.y + scroll.y - (top + height / 2)) * (strength as number);

          renderStyles.current.x.current = x;
          renderStyles.current.y.current = y;

          renderStyles.current.x.previous = lerp(
            renderStyles.current.x.previous,
            renderStyles.current.x.current,
            amt?.x,
          );
          renderStyles.current.y.previous = lerp(
            renderStyles.current.y.previous,
            renderStyles.current.y.current,
            amt?.y,
          );
          renderStyles.current.scale.previous = lerp(
            renderStyles.current.scale.previous,
            renderStyles.current.scale.current,
            amt?.scale,
          );

          updateElements();
        } else if (isHover) {
          setIsHover(false);
          resetTween([renderStyles.current.scale], 1);
          resetTween([renderStyles.current.x, renderStyles.current.y], 0, () => {
            updateElements();
          });
        }
      }
    },
    [
      elementRect,
      distanceToTrigger,
      isHover,
      renderStyles,
      amt,
      strength,
      scale,
      getScroll,
      isVisible,
      enabled,
      updateElements,
    ],
  );

  useEventListener('mousemove', onMouseMove);

  useIsomorphicLayoutEffect(() => {
    calculateSizeAndPosition();
  }, [calculateSizeAndPosition]);

  // Resize Method
  useIsomorphicLayoutEffect(() => {
    calculateSizeAndPosition();
  }, [windowSize, calculateSizeAndPosition]);

  useIsomorphicLayoutEffect(() => {
    calculateSizeAndPosition();
  }, [calculateSizeAndPosition]);

  return {
    isHover,
  };
}
